import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { TrendingUp, TrendingDown, Minus, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface MetricCardProps {
  title: string;
  value?: number | string;
  change?: number;
  changeLabel?: string;
  trend?: 'up' | 'down' | 'neutral';
  prefix?: string;
  suffix?: string;
  loading?: boolean;
  format?: 'number' | 'currency' | 'percent';
  decimals?: number;
}

export function MetricCard({
  title,
  value,
  change,
  changeLabel,
  trend,
  prefix,
  suffix,
  loading = false,
  format = 'number',
  decimals = 0,
}: MetricCardProps) {
  // Format value based on type
  const formatValue = (val: number | string | undefined): string => {
    if (val === undefined || val === null) return '-';
    if (typeof val === 'string') return val;

    const numVal = Number(val);
    if (isNaN(numVal)) return '-';

    switch (format) {
      case 'currency':
        return new Intl.NumberFormat('id-ID', {
          style: 'currency',
          currency: 'IDR',
          minimumFractionDigits: decimals,
          maximumFractionDigits: decimals,
        }).format(numVal);

      case 'percent':
        return `${numVal.toFixed(decimals)}%`;

      case 'number':
      default:
        return new Intl.NumberFormat('id-ID', {
          minimumFractionDigits: decimals,
          maximumFractionDigits: decimals,
        }).format(numVal);
    }
  };

  // Determine trend color
  const getTrendColor = (trendValue?: 'up' | 'down' | 'neutral', changeValue?: number): string => {
    if (!trendValue && changeValue !== undefined) {
      if (changeValue > 0) return 'text-green-600';
      if (changeValue < 0) return 'text-red-600';
      return 'text-gray-600';
    }

    switch (trendValue) {
      case 'up':
        return 'text-green-600';
      case 'down':
        return 'text-red-600';
      case 'neutral':
      default:
        return 'text-gray-600';
    }
  };

  // Get trend icon
  const TrendIcon = trend === 'up' ? TrendingUp : trend === 'down' ? TrendingDown : Minus;

  const trendColor = getTrendColor(trend, change);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium text-gray-700">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        ) : (
          <>
            <div className="text-2xl font-bold text-gray-900">
              {prefix}
              {formatValue(value)}
              {suffix}
            </div>

            {change !== undefined && (
              <div className={cn('flex items-center gap-1 text-xs mt-1', trendColor)}>
                <TrendIcon className="h-3 w-3" />
                <span className="font-medium">
                  {change > 0 ? '+' : ''}
                  {change.toFixed(1)}%
                </span>
                {changeLabel && <span className="text-gray-500 ml-1">{changeLabel}</span>}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
